import { Button, Drawer, Flex, Tabs, Typography } from 'antd';
import type { ButtonProps } from 'antd';
import styled from 'styled-components';

import { StyledModalWithHeaderBorder } from 'src/components/CommonStyledComponents/CommonStyledComponents.styled';
import { toCssUrl } from 'src/utils/sanitize';

export const StyledAssetModal = styled(StyledModalWithHeaderBorder)`
  .ant-modal-body {
    padding-top: 0;
  }
`;

export const StyledTabs = styled(Tabs)`
  .ant-tabs-nav {
    margin-bottom: 16px;
  }
`;

export const StyledAssetDrawer = styled(Drawer).attrs({
  closable: false,
  placement: 'bottom',
  size: 'auto',
  style: {
    borderTopLeftRadius: '6px',
    borderTopRightRadius: '6px',
  },
  styles: {
    header: {
      padding: '16px 20px 0',
      borderBottom: 'none',
    },
    body: {
      padding: '16px 20px',
      maxHeight: '70dvh',
      overflow: 'auto',
    },
    footer: {
      borderTop: 'none',
      padding: '0 20px 20px',
    },
  },
})``;

export const DrawerTitle = styled(Typography.Text)`
  font-size: 15px;
  font-weight: 600;
`;

export const AssetListGrid = styled.div<{ $isDesktop: boolean }>`
  display: grid;
  grid-template-columns: ${({ $isDesktop }) => ($isDesktop ? 'repeat(2, minmax(0, 1fr))' : '1fr')};
  gap: 12px;
  max-height: 340px;
  overflow-y: auto;
`;

export const AssetRow = styled(Flex).attrs({
  align: 'center',
  gap: 12,
})<{ $isSelected: boolean }>`
  padding: 8px 12px;
  border: 1px solid ${({ theme, $isSelected }) => ($isSelected ? theme.colorPrimary : theme.colorBorder)};
  border-radius: 8px;
  cursor: pointer;
  min-width: 0;

  &:hover {
    border-color: ${({ theme }) => theme.colorPrimaryBorder};
  }
`;

export const AssetThumbnail = styled.div<{ $preview?: string }>`
  width: 40px;
  height: 40px;
  border-radius: 4px;
  flex-shrink: 0;
  background-color: ${({ theme }) => theme.colorFillQuaternary};
  ${({ $preview }) =>
    $preview &&
    `
    background-image: ${toCssUrl($preview)};
    background-size: contain;
    background-position: center center;
    background-repeat: no-repeat;
  `}
`;

export const AssetRowText = styled(Flex).attrs({
  vertical: true,
  flex: 1,
})`
  min-width: 0;
`;

export const AssetName = styled(Typography.Text).attrs({ ellipsis: true })`
  color: ${({ theme }) => theme.colorPrimary};
`;

export const AssetMeta = styled(Typography.Text)`
  color: ${({ theme }) => theme.colorTextDescription};
  font-size: 12px;
`;

export const AssetGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  max-height: 50dvh;
  overflow-y: auto;
`;

export const AssetGridItem = styled.button<{ $isSelected: boolean; $preview?: string }>`
  aspect-ratio: 1;
  padding: 8px;
  border: 1px solid ${({ theme, $isSelected }) => ($isSelected ? theme.colorPrimary : theme.colorBorder)};
  border-radius: 8px;
  background-color: transparent;
  background-image: ${({ $preview }) => toCssUrl($preview ?? '')};
  background-size: contain;
  background-position: center center;
  background-repeat: no-repeat;
  background-origin: content-box;
  cursor: pointer;
  overflow: hidden;
`;

export const AssetGridItemFallback = styled(Typography.Text).attrs({ ellipsis: true })`
  font-size: 11px;
  color: ${({ theme }) => theme.colorTextDescription};
`;

export const EmptyStateWrapper = styled(Flex).attrs({
  align: 'center',
  justify: 'center',
})`
  min-height: 120px;
  color: ${({ theme }) => theme.colorTextDescription};
`;

export const SelectAssetLink = styled(Button).attrs(
  (): Pick<ButtonProps, 'type' | 'size'> => ({
    type: 'link',
    size: 'small',
  }),
)`
  padding: 0;
  height: auto;
  font-size: inherit;
`;

export const DrawerActions = styled(Flex).attrs({
  vertical: true,
})`
  width: 100%;
`;

export const DrawerActionButton = styled(Button).attrs(
  (): Pick<ButtonProps, 'type' | 'block'> => ({
    type: 'text',
    block: true,
  }),
)<{ $withBorder?: boolean; $bolder?: boolean }>`
  color: ${({ theme }) => theme.colorPrimary};
  height: 45px;
  border-bottom: ${({ theme, $withBorder }) => ($withBorder ? `1px solid ${theme.colorBorder}` : 'none')};
  border-radius: 0;
  font-weight: ${({ $bolder }) => ($bolder ? '600' : 'normal')};
  font-size: 18px;
`;

export const DrawerFooterButtons = styled(Flex).attrs({
  gap: 12,
})`
  width: 100%;

  .ant-btn {
    flex: 1;
  }
`;
